import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PAGE_SIZES, usePersistedPagination } from "../utils/usePersistedPagination";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./CompanyProjectsHome.css";

import { Tooltip } from "@mui/material";
import ContentCard from "../components/ContentCard";
import {
  ensureCompaniesAndProjectsLoaded,
  getCompaniesData,
  getProjectsForCompany,
  refreshForCurrentRoute,
} from "../utils/sessionStorageLayer";
import { appSession, KEYS } from "../utils/storage";
import { showToast } from "../utils/toastOptions";
import { usePermissionTemplate } from "../utils/usePermissionTemplate";
import { useUserSession } from "../utils/useUserSession";

/**
 * CompanyProjectsHome - Unified component for maintenance pages
 *
 * To add a new maintenance page:
 * 1. Add a new configuration object to PAGE_CONFIGS with your route path as the key
 * 2. Configure all the properties
 * 3. Add the route to App.js using this same component
 */

// Page configuration - only used for /home route
const PAGE_CONFIG = {
  title: "Home",
  showCompanyDirectoryButton: true,
  showRefreshButton: true,
  fetchProjectsWithUsers: false,
};

// Define consistent tool order (outside component to avoid recreation)
const TOOL_ORDER_MAP = new Map([
  ["directory-maintenance", 0],
  ["forecast", 1],
  ["change-management", 2],
]);

const getToolOrder = (tool) => TOOL_ORDER_MAP.get(tool.toLowerCase()) ?? 999;
const CompanySection = ({
  company,
  filteredProjects,
  permissionTemplate,
  pageConfig,
  maxProjectNameWidth,
  user,
  expanded,
  onToggle,
}) => {
  // Check if user has admin access (level 4) for a specific company
  // Template is fetched from backend session bundle, not from localStorage
  const companyId = company.company_id || company.id;
  const companyIdStr = String(companyId);
  const companyIdNum = Number(companyId);
  const templateEntry =
    permissionTemplate?.[companyIdStr] ||
    permissionTemplate?.[companyIdNum] ||
    permissionTemplate?.[companyId];
  const hasCompanyDirectoryAccess = templateEntry?.user_access_levels?.admin === 4;

  const hasError = filteredProjects.status !== 200;

  const allProjects = filteredProjects.projects ?? [];
  const rowsPerPageOptions = PAGE_SIZES;
  const [pagination, setPagination] = usePersistedPagination(
    company?.id != null ? `home-projects:${company.id}` : null
  );
  const page = pagination.pageIndex;
  const rowsPerPage = pagination.pageSize;
  const setPage = (pageOrUpdater) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: typeof pageOrUpdater === "function" ? pageOrUpdater(prev.pageIndex) : pageOrUpdater,
    }));
  };
  const setRowsPerPage = (size) => {
    setPagination({ pageIndex: 0, pageSize: size });
  };

  const pageCount = Math.ceil(allProjects.length / rowsPerPage);
  const safePage = page >= pageCount ? 0 : page;
  const paginatedProjects = allProjects.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage
  );
  const showPagination = allProjects.length > 10;

  return (
    <ContentCard
      elevation={1}
      p={0}
      sx={{
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Accordion
        expanded={!hasError && expanded}
        onChange={hasError ? undefined : onToggle}
        disabled={hasError}
        sx={{
          boxShadow: "none",
          backgroundColor: "transparent",
          "&:before": { display: "none" },
        }}
        slotProps={{ transition: { unmountOnExit: true } }}
      >
        <AccordionSummary
          expandIcon={hasError ? null : <ExpandMoreIcon />}
          aria-controls="panel3-content"
          id={`company-${company.id}`}
          sx={{ alignItems: "center" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography
                component="span"
                fontWeight="bold"
                style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "12px" }}
              >
                Company: {company.name}
                {hasError && (
                  <Typography
                    variant="body2"
                    sx={{
                      marginLeft: "16px",
                      color: "error.main",
                      fontStyle: "italic",
                      fontWeight: 500,
                    }}
                  >
                    {filteredProjects.error}
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ID: {company.id}
              </Typography>
            </div>
            {pageConfig.showCompanyDirectoryButton && hasCompanyDirectoryAccess && (
              <Button
                component={Link}
                to={`/company/${company.id}`}
                variant="outlined"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                }}
                sx={{ marginLeft: "auto" }}
                disabled={!user?.subscribed_tools?.[company.id]?.includes("directory-maintenance")}
              >
                Company Directory
              </Button>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          {allProjects.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ marginLeft: "16px", color: "text.secondary", fontStyle: "italic" }}
            >
              No projects
            </Typography>
          ) : (
            <>
              <ul className="project-list">
                {paginatedProjects.map((p) => (
                  <li key={p.id}>
                    <Tooltip title={`Project ID: ${p.id}`} followCursor>
                      <Button
                        component={Link}
                        sx={{
                          textTransform: "none",
                          minWidth: `${maxProjectNameWidth}px`,
                          justifyContent: "flex-start",
                        }}
                      >
                        {p.name}
                      </Button>
                    </Tooltip>
                    {(() => {
                      const subscribedTools = user?.subscribed_tools?.[company.id] ?? [];
                      if (subscribedTools.length === 0) {
                        return (
                          <Typography
                            variant="body2"
                            sx={{
                              marginLeft: "16px",
                              color: "text.secondary",
                              fontStyle: "italic",
                            }}
                          >
                            No subscriptions
                          </Typography>
                        );
                      }

                      const sortedTools = subscribedTools.slice().sort((a, b) => {
                        return getToolOrder(a) - getToolOrder(b);
                      });

                      return sortedTools.map((t) => {
                        const toolConfig = {
                          "directory-maintenance": {
                            title: "Directory Maintenance",
                            path: "/project",
                            icon: DashboardIcon,
                          },
                          forecast: {
                            title: "Forecast",
                            path: "/forecast",
                            icon: ShowChartIcon,
                          },
                          "change-management": {
                            title: "Change Management",
                            path: "/change-management",
                            icon: ChangeHistoryIcon,
                          },
                        }[t.toLowerCase()] || {
                          title: t,
                          path: "/project",
                          icon: DashboardIcon,
                        };

                        const IconComponent = toolConfig.icon;
                        return (
                          <Tooltip
                            key={`${company.id}-${p.id}-${t}`}
                            title={toolConfig.title}
                            placement="top"
                          >
                            <IconButton
                              component={Link}
                              to={`${toolConfig.path}/${company.id}/${p.id}`}
                              sx={{ marginLeft: "8px" }}
                              color="primary"
                              size="small"
                            >
                              <IconComponent />
                            </IconButton>
                          </Tooltip>
                        );
                      });
                    })()}
                  </li>
                ))}
              </ul>
              {showPagination && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 2,
                    borderTop: 1,
                    borderColor: "divider",
                    pt: 1,
                    pl: 1,
                    pr: 1
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" component="span">
                      Rows per page
                    </Typography>
                    <Select
                      size="small"
                      variant="standard"
                      disableUnderline
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(0);
                      }}
                      sx={{
                        fontSize: "0.875rem",
                        "& .MuiSelect-select": { py: 0.5, pr: 3 },
                      }}
                    >
                      {rowsPerPageOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  {pageCount > 1 && (
                    <Pagination
                      count={pageCount}
                      page={safePage + 1}
                      onChange={(_, newPage) => setPage(newPage - 1)}
                      showFirstButton
                      showLastButton
                    />
                  )}
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </ContentCard>
  );
};

CompanySection.propTypes = {
  company: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    company_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
  }).isRequired,
  filteredProjects: PropTypes.shape({
    status: PropTypes.number.isRequired,
    error: PropTypes.string,
    company_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    projects: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
      })
    ),
  }).isRequired,
  permissionTemplate: PropTypes.object,
  pageConfig: PropTypes.object.isRequired,
  maxProjectNameWidth: PropTypes.number.isRequired,
  user: PropTypes.object,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

export default function CompanyProjectsHome() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, error: userError } = useUserSession();
  const [searchTerm, setSearchTerm] = useState("");
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAccordions, setExpandedAccordions] = useState(
    () => appSession.get(KEYS.homeAccordions) || {}
  );

  const toggleAccordion = useCallback((companyId) => {
    setExpandedAccordions((prev) => {
      const next = { ...prev, [String(companyId)]: !prev[String(companyId)] };
      appSession.set(KEYS.homeAccordions, next);
      return next;
    });
  }, []);

  // This component should only be used for /home route
  // If accessed from another route, redirect to home
  useEffect(() => {
    if (location.pathname !== "/home") {
      navigate("/home", { replace: true });
    }
  }, [location.pathname, navigate]);

  const pageConfig = PAGE_CONFIG;

  // Fetch permission template from backend (not stored client-side)
  const { template: permissionTemplate } = usePermissionTemplate();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    ensureCompaniesAndProjectsLoaded(false)
      .then(() => {
        if (!cancelled) {
          setTick((t) => t + 1);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e);
          setLoading(false);
          showToast("error", e?.message || "Failed to load data");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      await refreshForCurrentRoute(location.pathname);
      setTick((t) => t + 1);
      showToast("success", "Data refreshed successfully");
    } catch (e) {
      showToast("error", e?.message || "Failed to refresh data");
    } finally {
      setLoading(false);
    }
  }, [location.pathname]);

  // tick is intentional: when session storage is updated we bump it so these memos re-read from storage
  const companiesData = useMemo(() => getCompaniesData() ?? {}, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const companies = useMemo(
    () =>
      Object.values(companiesData)
        .map((d) => d?.company)
        .filter(Boolean),
    [companiesData]
  );

  // Calculate maximum project name width across all companies' projects for consistent button spacing
  const maxProjectNameWidth = useMemo(() => {
    const companyList = Object.values(companiesData || {})
      .map((d) => d?.company)
      .filter(Boolean);
    const allProjects = companyList.flatMap((c) => getProjectsForCompany(c.id));
    if (allProjects.length === 0) return 200; // Default width
    const maxLength = Math.max(...allProjects.map((p) => (p.name || "").length));
    return Math.max(200, maxLength * 8);
  }, [companiesData]);

  // Show error toast when errors occur
  useEffect(() => {
    if (error) {
      const errorMessage = error?.message || String(error) || "An error occurred";
      showToast("error", errorMessage);
    }
  }, [error]);

  return (
    <main className="page dash-wrapper">
      <div
        style={{
          marginBottom: "1.5rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
            {pageConfig.title}
          </Typography>
          {pageConfig.showRefreshButton && (
            <Button
              variant="contained"
              color="success"
              onClick={fetchData}
              startIcon={<RefreshIcon />}
              sx={{ minWidth: "120px" }}
              disabled={loading}
            >
              REFRESH
            </Button>
          )}
        </div>
        <TextField
          size="small"
          placeholder="Search companies or projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "action.active", fontSize: "20px" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 300,
            maxWidth: 400,
            "& .MuiOutlinedInput-root": {
              backgroundColor: "background.paper",
              "&:hover": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                },
              },
              "&.Mui-focused": {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "primary.main",
                  borderWidth: "2px",
                },
              },
            },
          }}
        />
      </div>

      {loading ? (
        <div className="spinner-overlay">
          <div className="spinner" />
        </div>
      ) : (
        <div className="companies-list">
          {companies.length === 0 ? (
            <Typography
              variant="body1"
              sx={{ padding: "2rem", textAlign: "center", color: "text.secondary" }}
            >
              No companies found
            </Typography>
          ) : (
            (() => {
              const term = searchTerm.trim().toLowerCase();
              const filteredCompanies = companies
                .map((company) => {
                  const key = String(company.id);
                  const companyEntry = companiesData[key] ?? {
                    status: 200,
                    company_id: company.id,
                  };
                  const actualProjects = getProjectsForCompany(company.id);
                  const filteredProjects = term
                    ? actualProjects.filter((p) => p.name?.toLowerCase().includes(term))
                    : actualProjects;

                  const companyMatches =
                    !term ||
                    company.name?.toLowerCase().includes(term) ||
                    String(company.id).includes(term);
                  const hasProjects = filteredProjects.length > 0;

                  return {
                    company,
                    filteredProjects: {
                      ...companyEntry,
                      projects: companyMatches ? actualProjects : filteredProjects,
                    },
                    visible: companyMatches || hasProjects,
                  };
                })
                .filter((c) => c.visible);

              if (filteredCompanies.length === 0) {
                return (
                  <Typography
                    variant="body1"
                    sx={{ padding: "2rem", textAlign: "center", color: "text.secondary" }}
                  >
                    No matching companies or projects
                  </Typography>
                );
              }

              return filteredCompanies.map(({ company, filteredProjects }) => (
                <CompanySection
                  key={company.id}
                  company={company}
                  filteredProjects={filteredProjects}
                  permissionTemplate={permissionTemplate}
                  pageConfig={pageConfig}
                  maxProjectNameWidth={maxProjectNameWidth}
                  user={user}
                  expanded={!!expandedAccordions[String(company.id)]}
                  onToggle={() => toggleAccordion(company.id)}
                />
              ));
            })()
          )}
        </div>
      )}
    </main>
  );
}
